import { Vegnettlenke, VegnettlenkeReferanse } from '@/domain/vegnett/Vegnettlenke';
import { Node } from '@/domain/vegnett/Node';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import { defaultColor } from '@/utils/ColorHelper';
import { kartutsnittFromWktStrings } from '@/utils/SpatialHelper';
export class ValgtState {
    static fromId(id: number): ValgtState {
        return new ValgtState([Vegobjekt.dummy(id, null)], []);
    }

    static fromIdAndType(id: number, typeId: number): ValgtState {
        return new ValgtState([Vegobjekt.dummy(id, typeId)], []);
    }

    static fromIdAndLenkeAndSegment(id: number, lenke: number, segment: number) {
        return ValgtState.emptySelection().withVegnettlenkeReferanse({
            veglenkesekvensid: id,
            veglenkenummer: lenke,
            segmentnummer: segment,
        });
    }

    static fromIdAndLenke(id: number, lenke: number) {
        return ValgtState.emptySelection().withVegnettlenkeReferanse({
            veglenkesekvensid: id,
            veglenkenummer: lenke,
        });
    }

    static emptySelection(): ValgtState {
        return new ValgtState([], []);
    }

    static vegobjekterSelection(vegobjekt: Vegobjekt[]): ValgtState {
        return new ValgtState(vegobjekt, []);
    }

    static vegobjektSelection(vegobjekt: Vegobjekt): ValgtState {
        return new ValgtState([vegobjekt], []);
    }

    static veglenkeSelection(veglenke: Vegnettlenke): ValgtState {
        return new ValgtState([], [veglenke]);
    }

    constructor(
        readonly vegobjekter: Vegobjekt[] = [],
        readonly vegnettlenker: Vegnettlenke[] = [],
        readonly linkQueries: Record<string, Vegnettlenke[]> = {},
        readonly nodes: Node[] = [],
        readonly clusterObjekter: Vegobjekt[] = [],
        readonly fetching: boolean = false,
        readonly color: string = defaultColor,
        readonly vegnettlenkeReferanse: VegnettlenkeReferanse | null = null
    ) {}

    override(modifyObject: { [P in keyof ValgtState]?: ValgtState[P] }): ValgtState {
        return Object.assign(Object.create(ValgtState.prototype), {
            ...this,
            ...modifyObject,
        });
    }

    withVegobjekter(vegobjekter: Vegobjekt[]): ValgtState {
        return this.override({ vegobjekter });
    }

    withVegnettlenker(vegnettlenker: Vegnettlenke[]) {
        return this.override({ vegnettlenker, vegnettlenkeReferanse: null });
    }

    withVegnettlenkeReferanse(vegnettlenkeReferanse: VegnettlenkeReferanse) {
        return this.override({ vegnettlenker: [], vegnettlenkeReferanse });
    }

    withColor(color: string) {
        return this.override({ color });
    }

    withLinkQuery(queryString: string, links: Vegnettlenke[]) {
        return this.override({
            linkQueries: { ...this.linkQueries, [queryString]: links },
        });
    }

    withLinkQueries(linkQueries: Record<string, Vegnettlenke[]>) {
        return this.override({ linkQueries });
    }

    withNodes(nodes: Node[]) {
        return this.override({ nodes });
    }

    withClusterObjekter(clusterObjekter: Vegobjekt[]) {
        return this.override({ clusterObjekter });
    }

    withFetching(fetching: boolean): ValgtState {
        return this.override({ fetching });
    }

    isSingle(): boolean {
        let length = 0;

        if (this.vegobjekter) length += this.vegobjekter.length;
        if (this.vegnettlenker) length += this.vegnettlenker.length;
        if (this.vegnettlenkeReferanse) length += 1;

        return length === 1;
    }

    isCluster(): boolean {
        return this.clusterObjekter.length > 1;
    }

    singleVegobjekt(): Vegobjekt | null {
        return this.vegobjekter?.length > 0 ? this.vegobjekter[0] : null;
    }

    singleVegnettlenke(): Vegnettlenke | null {
        return this.vegnettlenker?.length > 0 ? this.vegnettlenker[0] : null;
    }

    singleVegnettlenkeReferanse(): VegnettlenkeReferanse | null {
        return this.vegnettlenkeReferanse;
    }

    isEmpty() {
        return (
            (this.vegobjekter === null || this.vegobjekter.length === 0) &&
            (this.vegnettlenker === null || this.vegnettlenker.length === 0) &&
            this.vegnettlenkeReferanse === null
        );
    }

    getKartutsnitt() {
        const vegobjektGeometry = this.singleVegobjekt()?.geometri?.wkt;
        if (vegobjektGeometry) {
            return kartutsnittFromWktStrings(vegobjektGeometry);
        }

        const vegnettGeometry = this.singleVegnettlenke()?.geometri?.wkt;
        if (vegnettGeometry) {
            return kartutsnittFromWktStrings(vegnettGeometry);
        }

        throw new Error('Valgt objekt mangler geometri');
    }
}
